/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ApolloError, gql, useMutation } from "@apollo/client";

import { useErrorsOrSuccessHandler } from "graphql/errorHandlers";
import { ERROR_FRAGMENT, ErrorFragment } from "graphql/fragments";
import { INVOICE, InvoiceQuery } from "graphql/queries";

const DESTROY_INVOICE_LINE = gql`
  mutation ($id: ID!) {
    destroyInvoiceLine(input: { id: $id }) {
      errors {
        ...ErrorFragment
      }
    }
  }
  ${ERROR_FRAGMENT}
`;

interface Payload {
  errors: ErrorFragment[];
}

interface DestroyInvoiceLineMutation {
  destroyInvoiceLine: Payload | null;
}

export const useDestroyInvoiceLineMutation = () => {
  const handleErrors = useErrorsOrSuccessHandler();

  const [mutation, { data, loading }] = useMutation<
    DestroyInvoiceLineMutation,
    { id: string }
  >(DESTROY_INVOICE_LINE);

  const destroyInvoiceLine = (id: string, onSuccess?: () => void) =>
    mutation({
      variables: { id },
      update(cache) {
        cache.modify({
          fields: {
            node() {
              const invoice = cache.readQuery<InvoiceQuery>({
                query: INVOICE,
              });
              if (!invoice) return;

              const lines = invoice.node.lines.filter((line) => line.id !== id);

              cache.writeQuery<InvoiceQuery>({
                query: INVOICE,
                data: {
                  node: {
                    ...invoice.node,
                    lines,
                  },
                },
              });
            },
          },
        });
      },
    })
      .then(({ data, errors }) => {
        const dataErrors = data?.destroyInvoiceLine?.errors ?? [];

        handleErrors({ dataErrors, graphqlErrors: errors, onSuccess });
      })
      .catch((_error: ApolloError) => null);

  return {
    destroyInvoiceLine,
    isLoading: loading,
    errors: data?.destroyInvoiceLine?.errors ?? [],
  };
};
